import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { shortLocation } from '../../../utils/locationDisplay';
import { Card, Descriptions, Table, Space, Typography, Drawer, Dropdown, Menu, Checkbox, Button, Tag, Input, Row, Col, message } from 'antd';
import { SettingOutlined, SortAscendingOutlined, SortDescendingOutlined, ReloadOutlined, FileExcelOutlined } from '@ant-design/icons';
import { apiService } from '../../../services/apiService';

// removed unused Select/Option

type ProductCategory = {
  productCatCode: string;
  name: string;
  productCatName: string;
};

type ImageInfo = {
  fileName: string;
  filePath: string;
  imageType: string;
};

type Product = {
  id?: number;
  status?: string;
  skuCode: string;
  createdBy?: string | null;
  createdDate?: string | null;
  lastUpdatedBy?: string | null;
  lastUpdatedDate?: string | null;
  platformCode?: string;
  storefrontStoreCode?: string;
  fullSkuCode?: string;
  productCode?: string;
  skuName?: string;
  skuNameTchi?: string;
  skuNameZhCn?: string;
  primaryCategoryCode?: string;
  skuSDescEn?: string | null;
  skuSDescCh?: string | null;
  skuSDescZhCn?: string | null;
  skuLDescEn?: string | null;
  skuLDescCh?: string | null;
  skuLDescZhCn?: string | null;
  invoiceRemarksEn?: string;
  invoiceRemarksCh?: string;
  invoiceRemarksZhCn?: string;
  videoLink?: string | null;
  videoLinkEn?: string;
  videoLinkCh?: string;
  videoLinkZhCn?: string;
  barcode?: string;
  brandCode?: string;
  manuCountry?: string;
  weight?: number;
  weightUnit?: string;
  packHeight?: number;
  packDepth?: number;
  packLength?: number;
  packDimensionUnit?: string;
  packBoxType?: string;
  packSpecEn?: string;
  packSpecCh?: string;
  packSpecZhCn?: string;
  currencyCode?: string;
  originalPrice?: number;
  sellingPrice?: number;
  mallDollar?: number;
  mallDollarVip?: number;
  productReadyMethod?: string;
  deliveryMethod?: string;
  returnDays?: number;
  productReadyDays?: number;
  pickupDays?: string;
  pickupTimeslot?: string;
  colorEn?: string | null;
  colorCh?: string | null;
  colorZhCn?: string | null;
  size?: string | null;
  sizeSystem?: string | null;
  invisibleFlag?: string;
  colorFamilies?: string | null;
  featureStartTime?: string | null;
  featureEndTime?: string | null;
  voucherType?: string | null;
  voucherDisplayType?: string | null;
  userMax?: string | null;
  redeemStartDate?: string | null;
  urgent?: string | null;
  expiryType?: string;
  fixedRedemptionDate?: string | null;
  uponPurchaseDate?: string | null;
  finePrintEn?: string | null;
  finePrintCh?: string | null;
  finePrintZhCn?: string | null;
  cost?: number | null;
  removalServices?: string | null;
  field1?: string | null;
  value1?: string | null;
  field2?: string | null;
  value2?: string | null;
  field3?: string | null;
  value3?: string | null;
  discountText?: string;
  discountTextTchi?: string;
  discountTextZhCn?: string | null;
  style?: string;
  goodsType?: string;
  warrantyPeriodUnit?: string | null;
  warrantyPeriod?: number;
  warrantySupplierEn?: string | null;
  warrantySupplierCh?: string | null;
  warrantySupplierZhCn?: string | null;
  serviceCentreAddressEn?: string | null;
  serviceCentreAddressCh?: string | null;
  serviceCentreAddressZhCn?: string | null;
  serviceCentreEmail?: string | null;
  serviceCentreContact?: string | null;
  warrantyRemarkEn?: string | null;
  warrantyRemarkCh?: string | null;
  warrantyRemarkZhCn?: string | null;
  onOfflineStatus?: string;
  categories?: ProductCategory[];
  imagesMainPhotoList?: ImageInfo[];
  imagesProductPhotoList?: ImageInfo[];
  imagesOtherPhotoList?: ImageInfo[];
  imagesAdvertisingList?: ImageInfo[] | null;
  commissionRate?: number;
  brandNameEn?: string;
  brandNameTc?: string;
  brandNameZhCn?: string;
  isPrimarySku?: string;
  storeLandMarkFlag?: string;
  partnerInfo?: any;
  externalPlatform?: any;
  createdAt?: string;
  updatedAt?: string;
};

const fmt = (v?: string | number | null) => (v == null ? '-' : String(v));

interface APIResponse<T> {
  success: boolean;
  data: T;
  pagination?: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

const HKTVmallProduct: React.FC = () => {
  const [products, setProducts] = useState<Product[]>([]);
  const [selected, setSelected] = useState<Product | null>(null);
  const [sortField, setSortField] = useState<string>('skuCode');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [columnSettings, setColumnSettings] = useState<Record<string, { visible: boolean; order: number }>>({});
  const [loading, setLoading] = useState(false);
  const [stockLoading, setStockLoading] = useState(false);
  const [stockLocations, setStockLocations] = useState<Array<{ warehouse: string; location: string; qty: number; available: number; updatedAt: string }>>([]);
  
  // 分頁狀態
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);
  
  // 過濾條件狀態
  const [filters, setFilters] = useState({
    skuCode: '',
    productName: '',
  });

  useEffect(() => {
    const saved = localStorage.getItem('hktv-products-columns-config');
    if (saved) {
      try { setColumnSettings(JSON.parse(saved)); return; } catch {}
    }
    // default
    setColumnSettings({
      skuCode: { visible: true, order: 0 },
      productName: { visible: true, order: 1 },
      brand: { visible: true, order: 2 },
      barcode: { visible: true, order: 3 },
      originalPrice: { visible: true, order: 4 },
      sellingPrice: { visible: true, order: 5 },
      onOfflineStatus: { visible: true, order: 6 },
      goodsType: { visible: true, order: 7 },
      weight: { visible: true, order: 8 },
      packSpec: { visible: true, order: 9 },
    });
  }, []);


  const handleColumnVisibilityChange = (key: string, visible: boolean) => {
    const next = { ...columnSettings, [key]: { ...(columnSettings[key] || { order: 0 }), visible } };
    setColumnSettings(next);
    localStorage.setItem('hktv-products-columns-config', JSON.stringify(next));
  };

  const showAllColumns = () => {
    const all: Record<string, { visible: boolean; order: number }> = {};
    Object.keys(allColumns).forEach((k, idx) => { all[k] = { visible: true, order: idx }; });
    setColumnSettings(all);
    localStorage.setItem('hktv-products-columns-config', JSON.stringify(all));
  };

  const resetColumns = () => {
    const def = {
      skuCode: { visible: true, order: 0 },
      productName: { visible: true, order: 1 },
      brand: { visible: true, order: 2 },
      barcode: { visible: true, order: 3 },
      originalPrice: { visible: true, order: 4 },
      sellingPrice: { visible: true, order: 5 },
      onOfflineStatus: { visible: true, order: 6 },
      goodsType: { visible: true, order: 7 },
      weight: { visible: true, order: 8 },
      packSpec: { visible: true, order: 9 },
    } as Record<string, { visible: boolean; order: number }>;
    setColumnSettings(def);
    localStorage.setItem('hktv-products-columns-config', JSON.stringify(def));
  };

  const getSortIcon = (field: string) => {
    if (sortField !== field) return null;
    return sortOrder === 'asc' ? <SortAscendingOutlined style={{ color: '#1890ff', marginLeft: 4 }} /> : <SortDescendingOutlined style={{ color: '#1890ff', marginLeft: 4 }} />;
  };

  // 獲取產品數據
  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      const queryParams = new URLSearchParams();
      if (filters.skuCode) queryParams.append('sku_code', filters.skuCode);
      if (filters.productName) queryParams.append('product_name', filters.productName);
      queryParams.append('page', String(currentPage));
      queryParams.append('limit', String(pageSize));

      const endpoint = `/hktv-products?${queryParams.toString()}`;

      const response = await apiService.get<APIResponse<Product[]>>(endpoint);
      if (response.success) {
        setProducts(response.data || []);
        // 更新總數
        if (response.pagination) {
          setTotal(response.pagination.total);
        }
      } else {
        message.error('獲取產品數據失敗');
      }
    } catch (error) {
      console.error('Error fetching products:', error);
      message.error('獲取產品數據失敗');
      setProducts([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [filters.skuCode, filters.productName, currentPage, pageSize]);

  // 刷新數據函數
  const handleRefresh = () => {
    fetchProducts();
  };

  // 導出功能 - 使用CSV格式，導出所有數據
  const handleExportExcel = async () => {
    try {
      if (total === 0) {
        message.warning('沒有數據可導出');
        return;
      }

      setLoading(true);
      message.loading({ content: '正在準備導出數據...', key: 'export', duration: 0 });

      // 獲取所有數據（不分頁）
      const queryParams = new URLSearchParams();
      if (filters.skuCode) queryParams.append('sku_code', filters.skuCode);
      if (filters.productName) queryParams.append('product_name', filters.productName);
      queryParams.append('page', '1');
      queryParams.append('limit', String(total)); // 獲取所有數據

      const endpoint = `/hktv-products?${queryParams.toString()}`;
      const response = await apiService.get<APIResponse<Product[]>>(endpoint);
      
      if (!response.success || !response.data || response.data.length === 0) {
        message.error({ content: '獲取導出數據失敗', key: 'export' });
        return;
      }

      // 生成CSV內容
      const csvContent = generateCSVContent(response.data);

      // 創建Blob並下載
      const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `hktv_products_export_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      message.success({ content: `成功導出 ${response.data.length} 條產品數據`, key: 'export' });
    } catch (error) {
      console.error('導出失敗:', error);
      message.error({ content: '導出失敗，請稍後重試', key: 'export' });
    } finally {
      setLoading(false);
    }
  };

  // 生成CSV內容
  const generateCSVContent = (data: Product[]) => {
    const headers = [
      'SKU代碼', '產品名稱(中文)', '產品名稱(英文)', '品牌(中文)', '品牌(英文)', '品牌代碼', '條碼',
      '平台代碼', '店鋪代碼', '完整SKU代碼', '產品代碼', '主分類代碼', '製造國',
      '重量', '重量單位', '包裝高度', '包裝深度', '包裝長度', '包裝尺寸單位', '包裝類型',
      '包裝規格(中文)', '包裝規格(英文)', '貨幣代碼', '原價', '售價', '配送方式',
      '退貨天數', '產品準備天數', '取貨天數', '取貨時段', '在線狀態', '商品類型',
      '樣式', '傭金率', '是否主SKU', '店鋪地標標誌', '折扣文本(中文)', '折扣文本(英文)',
      '創建時間', '更新時間'
    ];

    const rows = data.map(product => [
      product.skuCode || '',
      product.skuNameTchi || '',
      product.skuName || '',
      product.brandNameTc || '',
      product.brandNameEn || '',
      product.brandCode || '',
      product.barcode || '',
      product.platformCode || '',
      product.storefrontStoreCode || '',
      product.fullSkuCode || '',
      product.productCode || '',
      product.primaryCategoryCode || '',
      product.manuCountry || '',
      product.weight || '',
      product.weightUnit || '',
      product.packHeight || '',
      product.packDepth || '',
      product.packLength || '',
      product.packDimensionUnit || '',
      product.packBoxType || '',
      product.packSpecCh || '',
      product.packSpecEn || '',
      product.currencyCode || '',
      product.originalPrice || '',
      product.sellingPrice || '',
      product.deliveryMethod || '',
      product.returnDays || '',
      product.productReadyDays || '',
      product.pickupDays || '',
      product.pickupTimeslot || '',
      product.onOfflineStatus || '',
      product.goodsType || '',
      product.style || '',
      product.commissionRate || '',
      product.isPrimarySku || '',
      product.storeLandMarkFlag || '',
      product.discountTextTchi || '',
      product.discountText || '',
      product.createdAt ? `\u200B${new Date(product.createdAt).toISOString().split('T')[0]}` : '',
      product.updatedAt ? `\u200B${new Date(product.updatedAt).toISOString().split('T')[0]}` : ''
    ]);

    return [headers, ...rows].map(row => row.join(',')).join('\n');
  };

  // 初始加載數據
  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  // 監聽過濾條件變化，防抖處理並重置到第一頁
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setCurrentPage(1); // 過濾條件變化時重置到第一頁
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [filters.skuCode, filters.productName]);


  // 在前端進行客戶端過濾（API已經處理了主要過濾，這裏作為額外的實時過濾）
  const filteredProducts = useMemo(() => {
    return products;
  }, [products]);

  const list = useMemo(() => (filteredProducts || []).map((p) => ({
    key: p.fullSkuCode || p.skuCode || Math.random().toString(36).slice(2),
    skuCode: p.skuCode,
    productName: p.skuNameTchi || p.skuName,
    brand: p.brandNameTc || p.brandNameEn || p.brandCode,
    barcode: p.barcode,
    originalPrice: `${p.currencyCode} ${p.originalPrice}`,
    sellingPrice: `${p.currencyCode} ${p.sellingPrice}`,
    onOfflineStatus: p.onOfflineStatus,
    goodsType: p.goodsType,
    weight: `${p.weight} ${p.weightUnit}`,
    packSpec: p.packSpecCh || p.packSpecEn,
    platformCode: p.platformCode,
    storefrontStoreCode: p.storefrontStoreCode,
    primaryCategoryCode: p.primaryCategoryCode,
    deliveryMethod: p.deliveryMethod,
    discountText: p.discountTextTchi || p.discountText,
    style: p.style,
    commissionRate: p.commissionRate,
    raw: p,
  }))
  .sort((a, b) => {
    const A = a[sortField as keyof typeof a];
    const B = b[sortField as keyof typeof b];
    const av = A == null ? '' : String(A);
    const bv = B == null ? '' : String(B);
    const cmp = av.localeCompare(bv);
    return sortOrder === 'asc' ? cmp : -cmp;
  })
  , [filteredProducts, sortField, sortOrder]);

  // 加載當前選中產品的庫位資訊
  const loadStockLocations = useCallback(async (sku?: string) => {
    if (!sku) { setStockLocations([]); return; }
    try {
      setStockLoading(true);
      const resp: any = await apiService.getStockList({ page: 1, limit: 100, search: sku, sortField: 'updatedAt', sortOrder: 'desc' });
      const rows = (resp?.data || []).map((r: any) => ({
        warehouse: r.warehouseName || r.warehouse || '-',
        location: r.locationCode || r.location || '-',
        qty: Number(r.total || r.stockQuantity || 0),
        available: Number(r.available || 0),
        updatedAt: r.updatedAt || r.updateTime || '',
      }));
      setStockLocations(rows);
    } catch (e) {
      setStockLocations([]);
    } finally {
      setStockLoading(false);
    }
  }, []);

  useEffect(() => {
    if (selected?.skuCode) {
      loadStockLocations(selected.skuCode);
    } else {
      setStockLocations([]);
    }
  }, [selected, loadStockLocations]);

  const handleColumnSort = (field: string) => {
    if (sortField === field) {
      setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  const COLUMN_LABELS: Record<string, string> = {
    skuCode: 'SKU代碼',
    productName: '產品名稱',
    brand: '品牌',
    barcode: '條碼',
    originalPrice: '原價',
    sellingPrice: '售價',
    onOfflineStatus: '狀態',
    goodsType: '商品類型',
    weight: '重量',
    packSpec: '包裝規格',
    platformCode: '平台',
    storefrontStoreCode: '店鋪代碼',
    primaryCategoryCode: '分類代碼',
    deliveryMethod: '配送方式',
    discountText: '折扣標籤',
    style: '樣式',
    commissionRate: '傭金率',
  };

  const allColumns: Record<string, any> = {
    skuCode: {
      title: (
        <div style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleColumnSort('skuCode')}>
          {COLUMN_LABELS.skuCode} {getSortIcon('skuCode')}
        </div>
      ),
      dataIndex: 'skuCode', width: 160,
    },
    productName: {
      title: (
        <div style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleColumnSort('productName')}>
          {COLUMN_LABELS.productName} {getSortIcon('productName')}
        </div>
      ),
      dataIndex: 'productName', ellipsis: true,
    },
    brand: {
      title: (
        <div style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleColumnSort('brand')}>
          {COLUMN_LABELS.brand} {getSortIcon('brand')}
        </div>
      ),
      dataIndex: 'brand', width: 120,
    },
    barcode: {
      title: (
        <div style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleColumnSort('barcode')}>
          {COLUMN_LABELS.barcode} {getSortIcon('barcode')}
        </div>
      ),
      dataIndex: 'barcode', width: 140,
    },
    originalPrice: {
      title: (
        <div style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleColumnSort('originalPrice')}>
          {COLUMN_LABELS.originalPrice} {getSortIcon('originalPrice')}
        </div>
      ),
      dataIndex: 'originalPrice', width: 120,
    },
    sellingPrice: {
      title: (
        <div style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleColumnSort('sellingPrice')}>
          {COLUMN_LABELS.sellingPrice} {getSortIcon('sellingPrice')}
        </div>
      ),
      dataIndex: 'sellingPrice', width: 120,
    },
    onOfflineStatus: {
      title: (
        <div style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleColumnSort('onOfflineStatus')}>
          {COLUMN_LABELS.onOfflineStatus} {getSortIcon('onOfflineStatus')}
        </div>
      ),
      dataIndex: 'onOfflineStatus', width: 100,
      render: (status: string) => (
        <Tag color={status === 'ONLINE' ? 'green' : 'orange'}>{status}</Tag>
      ),
    },
    goodsType: {
      title: (
        <div style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleColumnSort('goodsType')}>
          {COLUMN_LABELS.goodsType} {getSortIcon('goodsType')}
        </div>
      ),
      dataIndex: 'goodsType', width: 140, ellipsis: true,
    },
    weight: {
      title: (
        <div style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleColumnSort('weight')}>
          {COLUMN_LABELS.weight} {getSortIcon('weight')}
        </div>
      ),
      dataIndex: 'weight', width: 100,
    },
    packSpec: {
      title: (
        <div style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleColumnSort('packSpec')}>
          {COLUMN_LABELS.packSpec} {getSortIcon('packSpec')}
        </div>
      ),
      dataIndex: 'packSpec', width: 120,
    },
    platformCode: {
      title: (
        <div style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleColumnSort('platformCode')}>
          {COLUMN_LABELS.platformCode} {getSortIcon('platformCode')}
        </div>
      ),
      dataIndex: 'platformCode', width: 80,
    },
    storefrontStoreCode: {
      title: (
        <div style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleColumnSort('storefrontStoreCode')}>
          {COLUMN_LABELS.storefrontStoreCode} {getSortIcon('storefrontStoreCode')}
        </div>
      ),
      dataIndex: 'storefrontStoreCode', width: 120,
    },
    primaryCategoryCode: {
      title: (
        <div style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleColumnSort('primaryCategoryCode')}>
          {COLUMN_LABELS.primaryCategoryCode} {getSortIcon('primaryCategoryCode')}
        </div>
      ),
      dataIndex: 'primaryCategoryCode', width: 140,
    },
    deliveryMethod: {
      title: (
        <div style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleColumnSort('deliveryMethod')}>
          {COLUMN_LABELS.deliveryMethod} {getSortIcon('deliveryMethod')}
        </div>
      ),
      dataIndex: 'deliveryMethod', width: 160,
    },
    discountText: {
      title: (
        <div style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleColumnSort('discountText')}>
          {COLUMN_LABELS.discountText} {getSortIcon('discountText')}
        </div>
      ),
      dataIndex: 'discountText', width: 120,
    },
    style: {
      title: (
        <div style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleColumnSort('style')}>
          {COLUMN_LABELS.style} {getSortIcon('style')}
        </div>
      ),
      dataIndex: 'style', width: 80,
      render: (style: string) => (
        <Tag color={style === 'RED' ? 'red' : 'default'}>{style}</Tag>
      ),
    },
    commissionRate: {
      title: (
        <div style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleColumnSort('commissionRate')}>
          {COLUMN_LABELS.commissionRate} {getSortIcon('commissionRate')}
        </div>
      ),
      dataIndex: 'commissionRate', width: 100,
      render: (rate: number) => `${rate}%`,
    },
  };

  const columns = Object.entries(columnSettings)
    .filter(([_, cfg]) => cfg.visible)
    .sort(([, a], [, b]) => a.order - b.order)
    .map(([key]) => allColumns[key as keyof typeof allColumns])
    .filter(Boolean);

  const columnSettingsMenu = (
    <Menu>
      <Menu.Item key="header" disabled>
        列設置
      </Menu.Item>
      <Menu.Divider />
      {Object.keys(allColumns).map((key) => (
        <Menu.Item key={key}>
          <Checkbox checked={columnSettings[key]?.visible ?? true} onChange={(e) => handleColumnVisibilityChange(key, e.target.checked)}>
            {COLUMN_LABELS[key] || key}
          </Checkbox>
        </Menu.Item>
      ))}
      <Menu.Divider />
      <Menu.Item key="showAll" onClick={showAllColumns}>
        <Button type="link" size="small" style={{ padding: 0 }}>顯示所有列</Button>
      </Menu.Item>
      <Menu.Item key="reset" onClick={resetColumns}>
        <Button type="link" size="small" style={{ padding: 0 }}>重置列設置</Button>
      </Menu.Item>
    </Menu>
  );

  return (
    <Space direction="vertical" size={16} style={{ width: '100%' }}>
      <Card extra={
        <Space>
          <Input
            value={filters.skuCode}
            onChange={(e) => setFilters(prev => ({ ...prev, skuCode: e.target.value }))}
            placeholder="SKU代碼"
            allowClear
            style={{ width: 300 }}
          />
          <Input
            value={filters.productName}
            onChange={(e) => setFilters(prev => ({ ...prev, productName: e.target.value }))}
            placeholder="產品名稱"
            allowClear
            style={{ width: 300 }}
          />
          <Button
            icon={<ReloadOutlined />}
            size="small"
            onClick={handleRefresh}
            loading={loading}
          >
            刷新
          </Button>
          <Button
            icon={<FileExcelOutlined />}
            size="small"
            onClick={handleExportExcel}
            disabled={total === 0}
          >
            導出
          </Button>
          <Dropdown overlay={columnSettingsMenu} trigger={["click"]}>
            <Button icon={<SettingOutlined />} size="small">列設置</Button>
          </Dropdown>
        </Space>
      }>
        <Table
          rowKey="key"
          dataSource={list}
          columns={columns as any}
          scroll={{ x: 'max-content' }}
          pagination={{
            current: currentPage,
            pageSize: pageSize,
            total: total,
            showSizeChanger: true,
            showQuickJumper: true,
            pageSizeOptions: ['10', '20', '50', '100'],
            showTotal: (total, range) => `第 ${range[0]}-${range[1]} 條，共 ${total} 條`,
            onChange: (page, newPageSize) => {
              if (newPageSize !== pageSize) {
                setPageSize(newPageSize);
                setCurrentPage(1); // 改變每頁數量時重置到第一頁
              } else {
                setCurrentPage(page);
              }
            }
          }}
          loading={loading}
          onRow={(record) => ({
            onClick: () => setSelected(record.raw as Product),
          })}
        />
      </Card>

        <Drawer
        title={`產品詳情 - ${fmt(selected?.skuCode)}`}
        placement="right"
        width={720}
        open={!!selected}
        onClose={() => setSelected(null)}
      >
        {selected && (
          <Space direction="vertical" size={16} style={{ width: '100%' }}>
            <Row gutter={24}>
              <Col xs={24} md={8}>
                {selected.imagesMainPhotoList?.[0]?.filePath ? (
                  <img
                    src={selected.imagesMainPhotoList[0].filePath}
                    width="100%"
                    style={{ borderRadius: 6 }}
                    referrerPolicy="no-referrer"
                    alt="main"
                  />
                ) : (
                  <div style={{height:240,background:'#f5f5f5', display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                    無圖片
                  </div>
                )}
                <div style={{ marginTop: 12 }}>
                  {selected.imagesProductPhotoList?.slice(0,4).map((it)=> (
                    <img
                      key={it.fileName}
                      src={it.filePath}
                      width={80}
                      style={{ marginRight: 8, borderRadius: 4, border: '1px solid #f0f0f0' }}
                      referrerPolicy="no-referrer"
                      onError={(e)=>{ (e.currentTarget as HTMLImageElement).style.display = 'none' }}
                      alt={it.fileName}
                    />
                  ))}
                </div>
              </Col>
              <Col xs={24} md={16}>
                <Typography.Title level={4} style={{ marginTop: 0 }}>
                  {selected.skuNameTchi || selected.skuName}
                </Typography.Title>
                <Typography.Paragraph type="secondary">
                  {selected.fullSkuCode} • {selected.barcode} • {selected.brandNameTc || selected.brandCode}
                </Typography.Paragraph>
                <div style={{ marginBottom: 12 }}>
                  <Tag color="green">{selected.onOfflineStatus}</Tag>
                  <Tag>{selected.goodsType}</Tag>
                  <Tag color="red">{selected.style}</Tag>
                </div>
              </Col>
            </Row>

            <Card title="基本資訊">
              <Descriptions bordered size="small" column={2}>
                <Descriptions.Item label="平台">{fmt(selected.platformCode)}</Descriptions.Item>
                <Descriptions.Item label="店鋪">{fmt(selected.storefrontStoreCode)}</Descriptions.Item>
                <Descriptions.Item label="SKU代碼">{fmt(selected.skuCode)}</Descriptions.Item>
                <Descriptions.Item label="條碼">{fmt(selected.barcode)}</Descriptions.Item>
                <Descriptions.Item label="分類代碼" span={2}>{fmt(selected.primaryCategoryCode)}</Descriptions.Item>
                <Descriptions.Item label="品牌">{fmt(selected.brandNameTc || selected.brandNameEn || selected.brandCode)}</Descriptions.Item>
                <Descriptions.Item label="製造國">{fmt(selected.manuCountry)}</Descriptions.Item>
              </Descriptions>
            </Card>

            <Card title="倉庫位置資訊">
              <Table
                size="small"
                rowKey={(r) => `${r.warehouse}-${r.location}-${r.updatedAt}`}
                dataSource={stockLocations}
                loading={stockLoading}
                pagination={false}
                columns={[
                  { title: '倉庫', dataIndex: 'warehouse', width: 160 },
                  { title: '貨位', dataIndex: 'location', width: 120, render: (v: string) => shortLocation(v) },
                  { title: '庫存數量', dataIndex: 'qty', width: 120, align: 'right' as const },
                  { title: '可銷售數量', dataIndex: 'available', width: 140, align: 'right' as const },
                  { title: '更新時間', dataIndex: 'updatedAt', width: 200 },
                ]}
                locale={{ emptyText: '此 SKU 暫無庫位資料' }}
              />
            </Card>

            <Card title="商品簡述">
              <div dangerouslySetInnerHTML={{ __html: selected.skuSDescCh || selected.skuSDescEn || '' }} />
            </Card>

            {selected.categories && selected.categories.length > 0 && (
              <Card title="產品分類">
                <Descriptions bordered size="small" column={1}>
                  {selected.categories.map((c) => (
                    <Descriptions.Item key={c.productCatCode} label={c.productCatCode}>
                      {c.productCatName}
                    </Descriptions.Item>
                  ))}
                </Descriptions>
              </Card>
            )}
          </Space>
        )}
      </Drawer>
    </Space>
  );
};

export default HKTVmallProduct;